﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox, Badge, RadioButton, RangeSlider,
  Layout, BlockStack, InlineStack, Box, Spinner, Link,
} from '@shopify/polaris';
import { DeliveryIcon } from '@shopify/polaris-icons';
import { useFreeShippingBar } from '../../hooks/useFreeShippingBar';
import type { FreeShippingBarSettings } from '../../api/freeShippingBar';
import { IconBadge } from '../shared/IconBadge';

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function LivePreview({ settings, cartDollars }: { settings: FreeShippingBarSettings; cartDollars: number }) {
  const cartCents = Math.round(cartDollars * 100);
  const textColor = HEX_PATTERN.test(settings.text_color) ? settings.text_color : '#FFFFFF';

  const tiers = [
    { threshold_cents: settings.threshold_cents, reached_message: settings.reached_message, isBase: true },
    ...settings.additional_tiers.map((t) => ({ ...t, isBase: false })),
  ].sort((a, b) => a.threshold_cents - b.threshold_cents);

  const maxThreshold = tiers[tiers.length - 1].threshold_cents;
  const nextTier = tiers.find((t) => cartCents < t.threshold_cents);
  const fullyReached = !nextTier;

  const message = fullyReached
    ? `${settings.celebration_enabled ? '🎉 ' : ''}${tiers[tiers.length - 1].reached_message}`
    : nextTier!.isBase
      ? settings.below_threshold_message.replace('{remaining}', `$${((nextTier!.threshold_cents - cartCents) / 100).toFixed(2)}`)
      : `You're $${((nextTier!.threshold_cents - cartCents) / 100).toFixed(2)} away from: ${nextTier!.reached_message}`;

  const progressCap = fullyReached ? maxThreshold : nextTier!.threshold_cents;
  const pct = Math.max(0, Math.min(100, (cartCents / progressCap) * 100));

  return (
    <BlockStack gap="200">
      <div
        style={{
          position: 'relative',
          background: HEX_PATTERN.test(settings.bar_color) ? settings.bar_color : '#008060',
          color: textColor,
          padding: '10px 32px',
          borderRadius: settings.corner_radius,
          textAlign: 'center',
          boxShadow: fullyReached && settings.celebration_enabled ? `0 0 0 2px ${textColor}` : undefined,
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute', top: 8, right: 10, width: 16, height: 16, lineHeight: '16px',
            fontSize: 13, color: textColor, opacity: 0.8,
          }}
        >
          ×
        </span>
        <Text as="p" fontWeight="semibold">{message}</Text>
        <div style={{ position: 'relative', height: 6, width: '100%', maxWidth: 480, margin: '6px auto 0', background: 'rgba(255,255,255,0.35)', borderRadius: 999 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: textColor, borderRadius: 999, transition: 'width 0.3s ease', overflow: 'hidden' }} />
          {tiers.filter((t) => t.threshold_cents < progressCap).map((t, i) => (
            <div
              key={i}
              style={{
                position: 'absolute', top: '50%', left: `${(t.threshold_cents / progressCap) * 100}%`,
                width: 3, height: 12, background: 'rgba(255,255,255,0.9)', borderRadius: 2,
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}
        </div>
      </div>
      <Text as="p" variant="bodySm" tone="subdued">
        On your storefront this bar is pinned to the {settings.position} of the page (with a dismiss button, shown
        above) so it stays visible while shoppers browse.
        {tiers.length > 1 && ' Markers on the bar show each additional goal.'}
      </Text>
    </BlockStack>
  );
}

export function FreeShippingBarPanel() {
  const { settings, loading, saving, error, fetchSettings, save, setError } = useFreeShippingBar();
  const [form, setForm] = useState<FreeShippingBarSettings | null>(null);
  const [previewCart, setPreviewCart] = useState('25');
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Free Shipping Progress Bar">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Free Shipping Progress Bar">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load free shipping bar settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const colorsValid = HEX_PATTERN.test(form.bar_color) && HEX_PATTERN.test(form.text_color);

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Free Shipping Progress Bar"
      subtitle="Show shoppers a live progress bar toward free shipping, right on your storefront, to encourage bigger carts"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}
          {saved && (
            <Banner tone="success" onDismiss={() => setSaved(false)}>
              <p>Settings saved. Changes appear on your storefront within about a minute.</p>
            </Banner>
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                <InlineStack gap="300" blockAlign="center" wrap={false}>
                  <IconBadge icon={DeliveryIcon} color="green" />
                  <Text as="h2" variant="headingMd">Widget Settings</Text>
                </InlineStack>
                <Badge tone={form.enabled ? 'success' : undefined}>{form.enabled ? 'Enabled' : 'Disabled'}</Badge>
              </InlineStack>

              <Checkbox
                label="Show the free shipping bar on my storefront"
                checked={form.enabled}
                onChange={(v) => setForm({ ...form, enabled: v })}
              />

              <TextField
                label="Free shipping threshold"
                type="number"
                prefix="$"
                autoComplete="off"
                value={(form.threshold_cents / 100).toFixed(2)}
                onChange={(v) => setForm({ ...form, threshold_cents: Math.round((Number(v) || 0) * 100) })}
              />

              <TextField
                label="Message before threshold is reached"
                helpText="Use {remaining} where the live remaining amount should appear"
                autoComplete="off"
                value={form.below_threshold_message}
                onChange={(v) => setForm({ ...form, below_threshold_message: v })}
              />

              <TextField
                label="Message once free shipping is unlocked"
                autoComplete="off"
                value={form.reached_message}
                onChange={(v) => setForm({ ...form, reached_message: v })}
              />

              <Box>
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm" fontWeight="semibold">Additional goals (optional)</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Give shoppers a reason to keep adding to cart after free shipping — e.g. a free gift at $100, or
                    10% off at $150. Shown as extra markers on the same progress bar.
                  </Text>

                  {form.additional_tiers.map((tier, i) => (
                    <InlineStack key={i} gap="200" blockAlign="end" wrap={false}>
                      <Box minWidth="140px">
                        <TextField
                          label={`Goal ${i + 2} threshold`}
                          type="number"
                          prefix="$"
                          autoComplete="off"
                          value={(tier.threshold_cents / 100).toFixed(2)}
                          onChange={(v) => {
                            const next = [...form.additional_tiers];
                            next[i] = { ...tier, threshold_cents: Math.round((Number(v) || 0) * 100) };
                            setForm({ ...form, additional_tiers: next });
                          }}
                        />
                      </Box>
                      <Box width="100%">
                        <TextField
                          label="Reward message"
                          autoComplete="off"
                          placeholder="e.g. a FREE gift!"
                          value={tier.reached_message}
                          onChange={(v) => {
                            const next = [...form.additional_tiers];
                            next[i] = { ...tier, reached_message: v };
                            setForm({ ...form, additional_tiers: next });
                          }}
                        />
                      </Box>
                      <Button
                        variant="plain"
                        tone="critical"
                        onClick={() => setForm({ ...form, additional_tiers: form.additional_tiers.filter((_, j) => j !== i) })}
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  ))}

                  {form.additional_tiers.length < 2 && (
                    <Box>
                      <Button
                        onClick={() => setForm({
                          ...form,
                          additional_tiers: [...form.additional_tiers, { threshold_cents: form.threshold_cents + 5000, reached_message: "You've unlocked a FREE gift!" }],
                        })}
                      >
                        + Add another goal
                      </Button>
                    </Box>
                  )}
                </BlockStack>
              </Box>

              <InlineStack gap="400">
                <Box>
                  <Text as="p" variant="bodySm" tone="subdued">Bar color</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <input
                      type="color"
                      value={HEX_PATTERN.test(form.bar_color) ? form.bar_color : '#008060'}
                      onChange={(e) => setForm({ ...form, bar_color: e.target.value })}
                    />
                    <Box minWidth="100px">
                      <TextField label="" labelHidden autoComplete="off" value={form.bar_color} onChange={(v) => setForm({ ...form, bar_color: v })} />
                    </Box>
                  </InlineStack>
                </Box>

                <Box>
                  <Text as="p" variant="bodySm" tone="subdued">Text color</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <input
                      type="color"
                      value={HEX_PATTERN.test(form.text_color) ? form.text_color : '#FFFFFF'}
                      onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                    />
                    <Box minWidth="100px">
                      <TextField label="" labelHidden autoComplete="off" value={form.text_color} onChange={(v) => setForm({ ...form, text_color: v })} />
                    </Box>
                  </InlineStack>
                </Box>
              </InlineStack>

              {!colorsValid && (
                <Text as="p" tone="critical" variant="bodySm">Colors must be a 6-digit hex code, e.g. #008060.</Text>
              )}

              <Box>
                <Text as="p" variant="bodySm" tone="subdued">Bar position</Text>
                <InlineStack gap="400">
                  <RadioButton
                    label="Top of page"
                    checked={form.position === 'top'}
                    onChange={() => setForm({ ...form, position: 'top' })}
                  />
                  <RadioButton
                    label="Bottom of page"
                    checked={form.position === 'bottom'}
                    onChange={() => setForm({ ...form, position: 'bottom' })}
                  />
                </InlineStack>
              </Box>

              <RangeSlider
                label={`Corner radius (${form.corner_radius}px)`}
                min={0}
                max={24}
                step={2}
                value={form.corner_radius}
                onChange={(v) => setForm({ ...form, corner_radius: Array.isArray(v) ? v[0] : v })}
              />

              <Checkbox
                label="Celebrate when shoppers unlock free shipping"
                helpText="Adds a small highlight and 🎉 to the message once the threshold is reached"
                checked={form.celebration_enabled}
                onChange={(v) => setForm({ ...form, celebration_enabled: v })}
              />

              <Box>
                <Button variant="primary" loading={saving} disabled={!colorsValid} onClick={handleSave}>
                  Save Settings
                </Button>
              </Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Live Preview</Text>
              <TextField
                label="Simulate cart total"
                type="number"
                prefix="$"
                autoComplete="off"
                value={previewCart}
                onChange={setPreviewCart}
              />
              <LivePreview settings={form} cartDollars={Number(previewCart) || 0} />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
              <Text as="p" tone="subdued">
                Saving here configures the bar's content — you still need to turn it on for your live theme once:
                go to <strong>Online Store &rarr; Themes &rarr; Customize &rarr; App embeds</strong>, then enable{' '}
                <strong>Free Shipping Bar</strong>. This is a one-time toggle; all future edits you make here take
                effect automatically without touching the theme editor again.
              </Text>
              <Link url="https://help.shopify.com/en/manual/online-store/themes/theme-structure/extend/apps" external>
                Learn more about app embeds in Shopify's docs
              </Link>
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
